/**
 * This code was AUTOGENERATED using the codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type IAccountMeta,
  type IInstruction,
  type IInstructionWithAccounts,
  type IInstructionWithData,
  type ReadonlyAccount,
} from '@solana/kit';
import { LIGHTHOUSE_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';
import {
  LogLevel,
  getLogLevelDecoder,
  getLogLevelEncoder,
  getMintAccountAssertionDecoder,
  getMintAccountAssertionEncoder,
  type LogLevelArgs,
  type MintAccountAssertion,
  type MintAccountAssertionArgs,
} from '../types';

export const ASSERT_MINT_ACCOUNT_DISCRIMINATOR = 7;

export function getAssertMintAccountDiscriminatorBytes() {
  return getU8Encoder().encode(ASSERT_MINT_ACCOUNT_DISCRIMINATOR);
}

export type AssertMintAccountInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountTargetAccount extends string | IAccountMeta<string> = string,
  TRemainingAccounts extends readonly IAccountMeta<string>[] = [],
> = IInstruction<TProgram> &
  IInstructionWithData<Uint8Array> &
  IInstructionWithAccounts<
    [
      TAccountTargetAccount extends string
        ? ReadonlyAccount<TAccountTargetAccount>
        : TAccountTargetAccount,
      ...TRemainingAccounts,
    ]
  >;

export type AssertMintAccountInstructionData = {
  discriminator: number;
  logLevel: LogLevel;
  assertion: MintAccountAssertion;
};

export type AssertMintAccountInstructionDataArgs = {
  logLevel?: LogLevelArgs;
  assertion: MintAccountAssertionArgs;
};

export function getAssertMintAccountInstructionDataEncoder(): Encoder<AssertMintAccountInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['logLevel', getLogLevelEncoder()],
      ['assertion', getMintAccountAssertionEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: ASSERT_MINT_ACCOUNT_DISCRIMINATOR,
      logLevel: value.logLevel ?? LogLevel.Silent,
    })
  );
}

export function getAssertMintAccountInstructionDataDecoder(): Decoder<AssertMintAccountInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['logLevel', getLogLevelDecoder()],
    ['assertion', getMintAccountAssertionDecoder()],
  ]);
}

export function getAssertMintAccountInstructionDataCodec(): Codec<
  AssertMintAccountInstructionDataArgs,
  AssertMintAccountInstructionData
> {
  return combineCodec(
    getAssertMintAccountInstructionDataEncoder(),
    getAssertMintAccountInstructionDataDecoder()
  );
}

export type AssertMintAccountInput<
  TAccountTargetAccount extends string = string,
> = {
  /** Target account to be asserted */
  targetAccount: Address<TAccountTargetAccount>;
  logLevel?: AssertMintAccountInstructionDataArgs['logLevel'];
  assertion: AssertMintAccountInstructionDataArgs['assertion'];
};

export function getAssertMintAccountInstruction<
  TAccountTargetAccount extends string,
  TProgramAddress extends Address = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
>(
  input: AssertMintAccountInput<TAccountTargetAccount>,
  config?: { programAddress?: TProgramAddress }
): AssertMintAccountInstruction<TProgramAddress, TAccountTargetAccount> {
  // Program address.
  const programAddress = config?.programAddress ?? LIGHTHOUSE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    targetAccount: { value: input.targetAccount ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  const instruction = {
    accounts: [getAccountMeta(accounts.targetAccount)],
    programAddress,
    data: getAssertMintAccountInstructionDataEncoder().encode(
      args as AssertMintAccountInstructionDataArgs
    ),
  } as AssertMintAccountInstruction<TProgramAddress, TAccountTargetAccount>;

  return instruction;
}

export type ParsedAssertMintAccountInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly IAccountMeta[] = readonly IAccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Target account to be asserted */
    targetAccount: TAccountMetas[0];
  };
  data: AssertMintAccountInstructionData;
};

export function parseAssertMintAccountInstruction<
  TProgram extends string,
  TAccountMetas extends readonly IAccountMeta[],
>(
  instruction: IInstruction<TProgram> &
    IInstructionWithAccounts<TAccountMetas> &
    IInstructionWithData<Uint8Array>
): ParsedAssertMintAccountInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = instruction.accounts![accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      targetAccount: getNextAccount(),
    },
    data: getAssertMintAccountInstructionDataDecoder().decode(instruction.data),
  };
}
